// Builds the two-row grouped table header shared by every Actuals vs Budget
// report level: a band naming each time base (and the month / period it covers)
// above the short per-column labels.
//
// The spans are derived from the columns that are actually rendered, never
// hard-coded: the column presets and "Customise columns" hide supplementary
// columns, monthly phasing adds a per-month column for each displayed month, and
// a comparison version adds two more lifetime columns. Every caller feeds the
// same flags it uses to render its cells, so the header can't drift out of step
// with the body.

import moment from 'moment';

// Month / Reporting period / Lifetime, each Budget + Actuals + Variance.
export const FIXED_COLUMN_COUNT = 9;

const COLUMNS_PER_TIME_BASE = 3;

// The fixed and supplementary column labels are defined here rather than read
// from the report payload. A generated report caches the heading row it was built
// with, so a report generated before a label change would otherwise render its
// figures under the old headings; the cells themselves are keyed by name and are
// always current, so the labels should be too.
const FIXED_COLUMN_LABELS = () => [
    $t('Budget'),
    $t('Actuals'),
    $t('Variance'),
    $t('Budget'),
    $t('Actuals'),
    $t('Variance'),
    $t('LE Budget'),
    $t('Actuals'),
    $t('Var v LE'),
];

const SUPPLEMENTARY_COLUMN_LABELS = () => [$t('Committed'), $t('Uncommitted'), $t('%'), $t('Budget Risks'), $t('Budget Opportunities'), $t('Forecast')];

const MONTH_KEY_FORMAT = 'MM-YYYY';

const monthLabel = (month) => (month ? moment(month, MONTH_KEY_FORMAT).format('MMM YYYY') : '');

/**
 * "Jan–Jun 2026" when the range sits inside one year, "Nov 2025–Feb 2026" when
 * it straddles two. A single-month range collapses to that month.
 */
const periodLabel = (months) => {
    if (!months.length) {
        return '';
    }

    const start = moment(months[0], MONTH_KEY_FORMAT);
    const end = moment(months[months.length - 1], MONTH_KEY_FORMAT);

    if (start.isSame(end, 'month')) {
        return start.format('MMM YYYY');
    }

    if (start.year() === end.year()) {
        return `${start.format('MMM')}–${end.format('MMM YYYY')}`;
    }

    return `${start.format('MMM YYYY')}–${end.format('MMM YYYY')}`;
};

/**
 * @param {Object} options
 * @param {string[]} options.supplementaryKeys    Which supplementary columns exist, in server order.
 * @param {Object}   options.visibleColumns       Column-key → visible map.
 * @param {string[]} options.months               Every month in the reporting period ('MM-YYYY').
 * @param {string[]} options.displayedMonths      The subset rendered as per-month columns (empty when phasing is off).
 * @param {string}   options.rowLabel             Label for the sticky first column.
 * @param {boolean}  options.comparisonActive     Whether the two lifetime comparison columns are rendered.
 * @param {string}   options.comparisonName       Short name of the comparison version, for its column labels.
 * @returns {{groups: Object[], columns: Object[]}}
 */
export const buildGroupedHeaderRows = ({
    supplementaryKeys = [],
    visibleColumns = {},
    months = [],
    displayedMonths = [],
    rowLabel = '',
    comparisonActive = false,
    comparisonName = '',
}) => {
    const groups = [];
    const columns = [];

    // The group band leaves the row-label column blank and the label sits in the
    // second row, directly above the hierarchy it names.
    groups.push({ key: 'label', text: '', isLabel: true });
    columns.push({ key: 'label', text: rowLabel, isLabel: true });

    if (displayedMonths.length) {
        groups.push({ key: 'phasing', text: $t('Monthly phasing · Actuals'), colSpan: displayedMonths.length });
        displayedMonths.forEach((month, index) => {
            columns.push({ key: `month-${month}`, text: month, month, groupStart: index === 0 });
        });
    }

    const lastMonth = months.length ? months[months.length - 1] : null;

    groups.push({
        key: 'month',
        text: lastMonth ? `${$t('Month')} · ${monthLabel(lastMonth)}` : $t('Month'),
        colSpan: COLUMNS_PER_TIME_BASE,
    });
    groups.push({
        key: 'period',
        text: months.length ? `${$t('Reporting period')} · ${periodLabel(months)}` : $t('Reporting period'),
        colSpan: COLUMNS_PER_TIME_BASE,
    });
    groups.push({
        key: 'lifetime',
        text: `${$t('Lifetime')} · ${$t('Total project')}`,
        colSpan: COLUMNS_PER_TIME_BASE + (comparisonActive ? 2 : 0),
    });

    FIXED_COLUMN_LABELS().forEach((heading, index) => {
        columns.push({
            key: `fixed-${index}`,
            text: heading,
            // Every third column opens a new time base.
            groupStart: index % COLUMNS_PER_TIME_BASE === 0,
        });
    });

    if (comparisonActive) {
        columns.push({ key: 'comparison-budget', text: $t('{{name}} Budget', { name: comparisonName }), comparison: true });
        columns.push({ key: 'comparison-variance', text: $t('Var v {{name}}', { name: comparisonName }), comparison: true });
    }

    const supplementaryLabels = SUPPLEMENTARY_COLUMN_LABELS();
    const visibleSupplementary = supplementaryKeys.map((key, index) => ({ key, text: supplementaryLabels[index] })).filter(({ key }) => visibleColumns[key]);

    if (visibleSupplementary.length) {
        groups.push({
            key: 'supplementary',
            text: `${$t('Lifetime')} · ${$t('Commitments, Risks & Forecast')}`,
            colSpan: visibleSupplementary.length,
        });

        visibleSupplementary.forEach(({ text, key }, index) => {
            columns.push({ key: `supp-${key}`, text, percentage: text === '%', groupStart: index === 0 });
        });
    }

    return { groups, columns };
};
